import { useState } from "react";
import { useRecordContext, TextInput, useGetList, SelectInput, FormDataConsumer } from "react-admin";
import { useFormContext, useWatch } from 'react-hook-form';
import { Funeral } from "../types";
import { Stack, Box, Button, Collapse, TextField as MTextField } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import GooglePlacesAutocomplete from 'react-google-places-autocomplete';
import { useLoadGoogleMaps } from '../hooks/useLoadGoogleMaps';

// Canonical place-of-death types shared with the aura-app onboarding + the
// place-of-death facility list in `places`. `residence` reuses a home address;
// `other` opens Google Places.
//
// The `chsld` value is the canonical long-term-care type in both regions — it
// drives the Ontario Form 15 "Long Term Care" checkbox (contract.service.ts) —
// but its label is region-specific: Quebec staff see "CHSLD", Ontario staff see
// "Long-term care home". Pass the case region to get the right label.
const typeChoices = (region: string) => [
    { id: 'hospital', name: 'Hospital' },
    { id: 'chsld', name: region === 'ontario' ? 'Long-term care home' : 'CHSLD' },
    { id: 'palliative', name: 'Palliative care residence' },
    { id: 'residence', name: 'Residence' },
    { id: 'other', name: 'Other' },
];

// Map the lowercase place-of-death type to the capitalized `places.type` value.
const FACILITY_TYPE: Record<string, string> = {
    hospital: 'Hospital',
    chsld: 'CHSLD',
    palliative: 'Palliative',
};

const FuneralPlaceOfDeathInput = () => {
    const record = useRecordContext<Funeral>();
    const { setValue } = useFormContext();

    const podType = useWatch({ name: 'place_of_death_type' }) as string | undefined;
    const caseRegion = (record?.region || '').toLowerCase();
    const isFacilityType = !!podType && !!FACILITY_TYPE[podType];
    const isResidence = podType === 'residence';

    // Default the address section open when there's an existing structured
    // address, so the value is visible on open instead of hidden behind the
    // toggle. Residence always shows it (see addressExpanded below).
    const [showAddress, setShowAddress] = useState<boolean>(() => {
        const a = record?.place_of_death_address;
        return !!(a && typeof a === 'object' && (a.adresse || a.municipalite || a.codePostal || a.province));
    });
    // Residence: always expanded (that's the input we want them to fill).
    const addressExpanded = showAddress || isResidence;

    // Facilities for the selected type; region-filtered client-side so we can
    // include region-agnostic rows (region IS NULL).
    const { data: places } = useGetList(
        'places',
        { filter: { type: FACILITY_TYPE[podType || ''] || '', active: true }, pagination: { page: 1, perPage: 500 } },
        { enabled: isFacilityType },
    );
    const facilities = (places || []).filter(
        (p: any) => !caseRegion || !p.region || String(p.region).toLowerCase() === caseRegion,
    );

    // Google Places (Other) — fills the structured address fields.
    const { isLoaded: mapsLoaded } = useLoadGoogleMaps(process.env.REACT_APP_GOOGLE_MAPS_API_KEY);
    const [addressValue, setAddressValue] = useState<any>(null);

    const fillAddressFromFacility = (p: any) => {
        setValue('place_of_death', p.name, { shouldDirty: true });
        setValue('place_of_death_address.adresse', p.address || '', { shouldDirty: true });
        setValue('place_of_death_address.municipalite', p.city || '', { shouldDirty: true });
        setValue('place_of_death_address.province', p.state || '', { shouldDirty: true });
        setValue('place_of_death_address.codePostal', p.postal_code || '', { shouldDirty: true });
        setValue('place_of_death_address.pays', p.country || 'Canada', { shouldDirty: true });
    };

    const handleGooglePlaceChange = (selected: any) => {
        setAddressValue(selected);
        if (!selected?.value?.place_id || !window.google?.maps?.places) return;
        const service = new window.google.maps.places.PlacesService(document.createElement('div'));
        service.getDetails(
            // `geometry` is why nothing in this system has ever stored a
            // latitude: it was never asked for. The zone test needs
            // coordinates, and asking here costs nothing on a call already
            // being made.
            { placeId: selected.value.place_id, fields: ['address_components', 'name', 'geometry'] },
            (place: any, status: any) => {
                if (status !== window.google.maps.places.PlacesServiceStatus.OK || !place) return;
                let streetNumber = '', route = '', city = '', province = '', postalCode = '', country = '';
                place.address_components?.forEach((c: any) => {
                    if (c.types.includes('street_number')) streetNumber = c.long_name;
                    else if (c.types.includes('route')) route = c.long_name;
                    else if (c.types.includes('locality') || c.types.includes('sublocality_level_1')) city = c.long_name;
                    else if (c.types.includes('administrative_area_level_1')) province = c.short_name;
                    else if (c.types.includes('postal_code')) postalCode = c.long_name;
                    else if (c.types.includes('country')) country = c.short_name;
                });
                setValue('place_of_death_address.adresse', [streetNumber, route].filter(Boolean).join(' '), { shouldDirty: true });
                setValue('place_of_death_address.municipalite', city, { shouldDirty: true });
                setValue('place_of_death_address.province', province, { shouldDirty: true });
                setValue('place_of_death_address.codePostal', postalCode, { shouldDirty: true });
                setValue('place_of_death_address.pays', country || 'Canada', { shouldDirty: true });

                // Persist coordinates alongside the address so the transport
                // zone can be tested without geocoding the case again later.
                const loc = place.geometry?.location;
                if (loc) {
                  setValue('place_of_death_address.lat', loc.lat(), { shouldDirty: true });
                  setValue('place_of_death_address.lng', loc.lng(), { shouldDirty: true });
                  // Stamp what these coordinates describe, so a later hand-edit
                  // of the address invalidates them instead of silently
                  // pointing at the old place.
                  setValue(
                    'place_of_death_address.geocoded_from',
                    [[streetNumber, route].filter(Boolean).join(' '), city, province, postalCode]
                      .map((x) => (x ?? '').trim())
                      .filter(Boolean)
                      .join(', '),
                    { shouldDirty: true },
                  );
                }
                if (place.name && !record?.place_of_death) {
                    setValue('place_of_death', place.name, { shouldDirty: true });
                }
                setShowAddress(true);
                setTimeout(() => setAddressValue(null), 100);
            },
        );
    };

    return (
        <Stack spacing={1}>
            <Box>
                <SelectInput
                    source="place_of_death_type"
                    label="Place of Death Type"
                    helperText={false}
                    choices={typeChoices(caseRegion)}
                />
                <FormDataConsumer>
                    {({ formData }) =>
                        formData.place_of_death_type === 'other' && (
                            <TextInput
                                source="place_of_death_type_other"
                                label="Place of Death Type (Other)"
                                fullWidth
                                helperText={false}
                            />
                        )
                    }
                </FormDataConsumer>
            </Box>

            {/* Place-of-death NAME. Hidden for residence (there the address is
                what matters, so we expand the address section instead) and for
                facility types (the freeSolo facility autocomplete below is the
                editor + writes here via setValue). Shown as a plain field for
                "other"/unset. Always rendered so the value stays registered and
                submits with the form. */}
            {isResidence || isFacilityType ? (
                <TextInput hidden source="place_of_death" type="hidden" label={false}
                    InputProps={{ disableUnderline: true }} helperText={false} sx={{ margin: 0 }} />
            ) : (
                <TextInput
                    source="place_of_death"
                    label="Place of Death Name (facility / location)"
                    fullWidth
                    helperText="Name of the location or facility. The address goes in the section below."
                />
            )}

            {/* Facility picker for hospital / CHSLD / palliative — pulls from the
                curated `places` list, filtered by the case region. freeSolo so a
                facility not in the list can still be typed as a label. */}
            {isFacilityType && (
                <Stack sx={{ width: 400 }}>
                    <Autocomplete
                        id="place_of_death_facility"
                        freeSolo
                        options={facilities as any[]}
                        getOptionLabel={(o: any) => (typeof o === 'string' ? o : o.name || '')}
                        defaultValue={record?.place_of_death || ''}
                        onChange={(_, value) => {
                            if (value && typeof value === 'object') fillAddressFromFacility(value);
                            else if (typeof value === 'string') setValue('place_of_death', value, { shouldDirty: true });
                        }}
                        onInputChange={(_, v, reason) => {
                            if (reason === 'input') setValue('place_of_death', v, { shouldDirty: true });
                        }}
                        renderInput={(params) => <MTextField {...params} label="Facility" />}
                    />
                </Stack>
            )}

            {/* Google Places search for Other — auto-fills the address below. */}
            {podType === 'other' && (
                <Box sx={{ width: 400 }}>
                    {mapsLoaded ? (
                        <GooglePlacesAutocomplete
                            selectProps={{
                                value: addressValue,
                                onChange: handleGooglePlaceChange,
                                placeholder: 'Search for the address…',
                                isClearable: true,
                                styles: { menu: (p: any) => ({ ...p, zIndex: 9999 }) },
                            }}
                            autocompletionRequest={{ componentRestrictions: { country: ['ca', 'us'] } }}
                        />
                    ) : (
                        <MTextField disabled fullWidth label="Loading address search…" />
                    )}
                </Box>
            )}

            <Box>
                {/* Residence always shows the address (it's the value we want),
                    so the toggle is only offered for the other types. */}
                {!isResidence && (
                    <Button
                        size="small"
                        onClick={() => setShowAddress(!showAddress)}
                        endIcon={showAddress ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        sx={{ textTransform: 'none', color: 'text.secondary' }}
                    >
                        {showAddress ? 'Hide' : 'Edit'} Place of Death Address
                    </Button>
                )}
                <Collapse in={addressExpanded}>
                    <Box sx={{ mt: 1, pl: 1, borderLeft: '2px solid #e0e0e0' }}>
                        <TextInput source="place_of_death_address.adresse" label="Street Address" fullWidth helperText={false} />
                        <TextInput source="place_of_death_address.municipalite" label="City / Municipality" fullWidth helperText={false} />
                        <TextInput source="place_of_death_address.province" label="Province" fullWidth helperText={false} />
                        <TextInput source="place_of_death_address.codePostal" label="Postal Code" fullWidth helperText={false} />
                        <TextInput source="place_of_death_address.pays" label="Country" fullWidth helperText={false} />
                        <TextInput source="place_of_death_code" label="Hospital Code" fullWidth helperText={false} />
                    </Box>
                </Collapse>
            </Box>
        </Stack>
    );
}

export default FuneralPlaceOfDeathInput;
